import { isSearchTextEmptyOrUndefined } from '#PRIVATE_SelectCommon';
import { useCallback, useEffect, useRef } from 'preact/hooks';
import type { useSelectSingle } from './useSelectSingle';

type Key = string | number;
type Data = Record<string, any>;

type PickedPropsFromUseSelectSingle<K extends Key, D extends Data> = Pick<
  ReturnType<typeof useSelectSingle<K, D>>,
  | 'clearValue'
  | 'inputRef'
  | 'isDropdownOpen'
  | 'setDropdownOpen'
  | 'setUserInput'
  | 'stopFiltering'
  | 'userInput'
>;
export type UseMobileDropdownProps<K extends Key, D extends Data> = PickedPropsFromUseSelectSingle<
  K,
  D
> & {
  displayValue: string;
};

export function useMobileDropdown<K extends Key, D extends Data>({
  clearValue,
  displayValue,
  inputRef,
  isDropdownOpen,
  userInput,
  setDropdownOpen,
  stopFiltering,
  setUserInput
}: UseMobileDropdownProps<K, D>) {
  // Create a ref for the input in the dropdown
  const dropdownInputRef = useRef<HTMLInputElement>(null);

  const closeDropdown = useCallback(() => {
    setDropdownOpen(false);
    // stop filtering before closing the dropdown
    stopFiltering();
    // revert the focus back to the main field
    inputRef.current?.focus();
  }, [inputRef, setDropdownOpen, stopFiltering]);

  const onBackButtonClick = useCallback(() => {
    closeDropdown();
  }, [closeDropdown]);

  const onClearButtonClick = useCallback(() => {
    // reset the user filter and the value of the component
    stopFiltering();
    // clear the value but do not close the dropdown
    clearValue(false);
    // set focus to the dropdown input field
    dropdownInputRef.current?.focus();
  }, [clearValue, stopFiltering]);

  const wasDropdownOpen = useRef(isDropdownOpen);
  useEffect(() => {
    // if the dropdown is open, ignore any valueItems update
    if (isDropdownOpen) {
      // this dropdown is opened now, so set the focus on the input field
      setTimeout(() => dropdownInputRef.current?.focus(), 0);
    }
    wasDropdownOpen.current = isDropdownOpen;
  }, [isDropdownOpen]);

  const isInitialRender = useRef(true);
  if (isInitialRender.current) {
    isInitialRender.current = false;
    setUserInput(displayValue);
  }

  return {
    selectSingleFieldInputProps: {
      displayValue: userInput ?? '',
      hasEmptyLabel: false,
      hasInsideLabel: true,
      inputRef: dropdownInputRef,
      isUserFiltering: true,
      removeIcon: 'always' as const,
      userInput
    },
    selectSingleFieldProps: {
      hasInsideLabel: true,
      isBackButtonShown: true,
      isClearButtonShown:
        !isSearchTextEmptyOrUndefined(userInput) || !isSearchTextEmptyOrUndefined(displayValue),
      isDropdownArrowShown: false,
      styleVariant: 'embedded' as const,
      onBackButtonClick,
      onClearButtonClick
    }
  };
}
